import { absoluteUrl, DEFAULT_OG_IMAGE, truncateDescription } from "./siteConfig";

export const BOURBON_CITY_CANONICAL_PATH = "/bourbon-city-spectacular";

const stripHtml = (value) =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/** Matches Bourbon City slugs across Contentful naming styles. */
export const isBourbonCityMeetSlug = (slug) => {
  if (!slug) return false;
  const normalized = decodeURIComponent(String(slug)).toLowerCase();
  return (
    normalized.includes("bourbon-city-spectacular") ||
    normalized.includes("bourboncityspectacular")
  );
};

/** One primary URL per meet — Bourbon City always uses the marketing path. */
export const resolveMeetCanonicalPath = (slug, fields = {}) => {
  const custom = fields?.canonicalPath?.trim();
  if (custom) {
    return custom.startsWith("/") ? custom : `/${custom}`;
  }

  const entrySlug = fields?.slug || slug;
  if (isBourbonCityMeetSlug(entrySlug) || isBourbonCityMeetSlug(slug)) {
    return BOURBON_CITY_CANONICAL_PATH;
  }

  if (entrySlug) {
    return `/meet-info/${encodeURIComponent(entrySlug)}`;
  }

  return slug ? `/meet-info/${encodeURIComponent(slug)}` : BOURBON_CITY_CANONICAL_PATH;
};

const MONTH_INDEX = {
  january: 0,
  jan: 0,
  february: 1,
  feb: 1,
  march: 2,
  mar: 2,
  april: 3,
  apr: 3,
  may: 4,
  june: 5,
  jun: 5,
  july: 6,
  jul: 6,
  august: 7,
  aug: 7,
  september: 8,
  sep: 8,
  sept: 8,
  october: 9,
  oct: 9,
  november: 10,
  nov: 10,
  december: 11,
  dec: 11,
};

const pad2 = (n) => String(n).padStart(2, "0");

const isoFromParts = (year, monthIndex, day) => {
  if (!year || monthIndex == null || !day) return null;
  return `${year}-${pad2(monthIndex + 1)}-${pad2(day)}`;
};

const extractYearHint = (...sources) => {
  for (const source of sources) {
    const text = stripHtml(source);
    if (!text) continue;
    const match = text.match(/\b(20\d{2})\b/);
    if (match) return Number(match[1]);
  }
  return null;
};

/** Parses common meet date strings from the Contentful `dates` field. */
export const parseDatesFromText = (text, yearHint = null) => {
  const cleaned = stripHtml(text);
  if (!cleaned) return null;

  const year = yearHint || extractYearHint(cleaned);

  // January 15-17, 2027  |  Jan 15 – 17, 2027
  const monthRange = cleaned.match(
    /([A-Za-z]+)\s+(\d{1,2})\s*[–\-—]\s*(\d{1,2})(?:,?\s*(20\d{2}))?/i
  );
  if (monthRange) {
    const month = MONTH_INDEX[monthRange[1].toLowerCase()];
    const resolvedYear = Number(monthRange[4]) || year;
    if (month != null && resolvedYear) {
      return {
        startDate: isoFromParts(resolvedYear, month, Number(monthRange[2])),
        endDate: isoFromParts(resolvedYear, month, Number(monthRange[3])),
      };
    }
  }

  // January 15, 2027
  const monthSingle = cleaned.match(/([A-Za-z]+)\s+(\d{1,2})(?:,?\s*(20\d{2}))?/i);
  if (monthSingle) {
    const month = MONTH_INDEX[monthSingle[1].toLowerCase()];
    const resolvedYear = Number(monthSingle[3]) || year;
    if (month != null && resolvedYear) {
      const startDate = isoFromParts(resolvedYear, month, Number(monthSingle[2]));
      return { startDate, endDate: startDate };
    }
  }

  // 1/15/2027 or 1/15/27
  const slashRange = cleaned.match(
    /(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?\s*[–\-—]\s*(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?/
  );
  if (slashRange) {
    const startYear = normalizeYear(slashRange[3], year);
    const endYear = normalizeYear(slashRange[6], startYear);
    if (startYear && endYear) {
      return {
        startDate: isoFromParts(startYear, Number(slashRange[1]) - 1, Number(slashRange[2])),
        endDate: isoFromParts(endYear, Number(slashRange[4]) - 1, Number(slashRange[5])),
      };
    }
  }

  const slashSingle = cleaned.match(/(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?/);
  if (slashSingle) {
    const resolvedYear = normalizeYear(slashSingle[3], year);
    if (resolvedYear) {
      const startDate = isoFromParts(
        resolvedYear,
        Number(slashSingle[1]) - 1,
        Number(slashSingle[2])
      );
      return { startDate, endDate: startDate };
    }
  }

  return null;
};

const normalizeYear = (rawYear, fallbackYear) => {
  if (rawYear) {
    const n = Number(rawYear);
    return n < 100 ? 2000 + n : n;
  }
  return fallbackYear || null;
};

const toSchemaDateTime = (isoDate) => {
  if (!isoDate) return null;
  // Kentucky meet times — date-only is valid; local morning default helps Google Events.
  return `${isoDate}T09:00:00-05:00`;
};

const resolveMeetSchemaImage = (pageData = {}) => {
  if (pageData.heroImageUrl?.startsWith("http")) return pageData.heroImageUrl;
  if (pageData.meetLogoUrl?.startsWith("http")) return pageData.meetLogoUrl;
  return absoluteUrl(DEFAULT_OG_IMAGE);
};

const toIsoDate = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString().slice(0, 10);
};

const formatDisplayDateRange = (startDate, endDate) => {
  if (!startDate) return "";

  const start = new Date(`${startDate}T12:00:00`);
  const end = endDate && endDate !== startDate ? new Date(`${endDate}T12:00:00`) : null;

  const monthDay = (date) =>
    date.toLocaleDateString("en-US", { month: "long", day: "numeric" });

  const year = start.getFullYear();

  if (!end) {
    return `${monthDay(start)}, ${year}`;
  }

  if (
    start.getMonth() === end.getMonth() &&
    start.getFullYear() === end.getFullYear()
  ) {
    return `${start.toLocaleDateString("en-US", { month: "long" })} ${start.getDate()}–${end.getDate()}, ${year}`;
  }

  return `${monthDay(start)} – ${monthDay(end)}, ${year}`;
};

/**
 * Uses Contentful `eventStartDate` / `eventEndDate` (Date fields).
 * Legacy `dates` rich text is still parsed when date fields are empty.
 */
export const parseMeetEventDates = (fields = {}) => {
  const startDate = toIsoDate(fields?.eventStartDate);
  const endDate = toIsoDate(fields?.eventEndDate) || startDate;

  if (startDate) {
    return {
      startDate: toSchemaDateTime(startDate),
      endDate: toSchemaDateTime(endDate || startDate),
      dateLabel: formatDisplayDateRange(startDate, endDate),
    };
  }

  const datesText = stripHtml(fields?.dates || "");
  const yearHint = extractYearHint(fields?.title, fields?.slug, datesText);
  const parsedFromText = parseDatesFromText(datesText, yearHint);

  if (parsedFromText?.startDate) {
    return {
      startDate: toSchemaDateTime(parsedFromText.startDate),
      endDate: toSchemaDateTime(parsedFromText.endDate || parsedFromText.startDate),
      dateLabel: formatDisplayDateRange(
        parsedFromText.startDate,
        parsedFromText.endDate || parsedFromText.startDate
      ),
    };
  }

  return { startDate: null, endDate: null, dateLabel: "" };
};

const defaultLevelsPhrase = (pageData) => {
  const stats = pageData?.quickStats || [];
  const levelsStat = stats.find((row) => /level|xcel|l1/i.test(row.label || ""));
  if (levelsStat?.label) {
    return levelsStat.label.replace(/^&\s*/, "and ");
  }
  return "Levels 1–10 and all Xcel divisions";
};

export const buildMeetSeoMeta = ({
  fields = {},
  pageData = {},
  venue = {},
  preferredSlug = "",
}) => {
  const title = fields?.title?.trim() || "Bourbon City Spectacular";
  const path = resolveMeetCanonicalPath(preferredSlug, fields);
  const dates = parseMeetEventDates(fields);
  const cityState = venue?.cityStateZip || "Bardstown, KY";
  const levelsPhrase = defaultLevelsPhrase(pageData);

  const seoTitle = dates.dateLabel
    ? `${title} — Kentucky Gymnastics Meet, ${dates.dateLabel}`
    : `${title} — Kentucky USAG Gymnastics Meet`;

  const seoDescription = truncateDescription(
    [
      `${title} is a USA Gymnastics sanctioned meet in ${cityState}.`,
      dates.dateLabel ? `Competition dates: ${dates.dateLabel}.` : null,
      `${levelsPhrase}.`,
      "Host gym registration, entry fees, venue, and spectator info.",
      "Register your team online.",
    ]
      .filter(Boolean)
      .join(" ")
  );

  return {
    title: seoTitle,
    description: seoDescription,
    path,
    dates,
    ogType: "website",
  };
};

export const buildMeetSportsEventSchema = ({
  fields = {},
  pageData = {},
  venue = {},
  preferredSlug = "",
  registrationUrl,
}) => {
  const seo = buildMeetSeoMeta({
    fields,
    pageData,
    venue,
    preferredSlug,
  });

  const image = resolveMeetSchemaImage(pageData);

  return {
    name: fields?.title?.trim() || "Bourbon City Spectacular",
    description: seo.description,
    path: seo.path,
    registrationUrl,
    locationName: venue?.name,
    streetAddress: venue?.street,
    addressLocality: "Bardstown",
    addressRegion: "KY",
    postalCode: venue?.cityStateZip?.match(/\b\d{5}\b/)?.[0] || "40004",
    startDate: seo.dates.startDate,
    endDate: seo.dates.endDate,
    image,
  };
};
